import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Link as LinkIcon,
  CheckCircle,
  XCircle,
  Calendar,
  Shield,
  MessageSquare,
  Package,
  BookOpen,
  ClipboardList,
  MessageCircle,
  PenLine,
  Upload,
  ExternalLink,
  FileText,
} from "lucide-react";
import { Timestamp } from "firebase/firestore";
import { format, differenceInDays, isPast } from "date-fns";
import { where, orderBy } from "firebase/firestore";
import clsx from "clsx";
import AccessRevocationTracker from "../../components/offboardings/AccessRevocationTracker";
import KnowledgeTracker from "../../components/offboardings/KnowledgeTracker";
import AssetReturnTracker from "../../components/offboardings/AssetReturnTracker";
import AuditLogTab from "../../components/offboardings/AuditLogTab";
import ApprovalPanel from "../../components/offboardings/ApprovalPanel";
import DependencyGraph from "../../components/offboardings/DependencyGraph";
import { CompleteOffboardingModal } from "../../components/offboardings/CompleteOffboardingModal";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Progress } from "../../components/ui/Progress";
import { LoadingSpinner } from "../../components/shared/LoadingSpinner";
import { useAuth } from "../../hooks/useAuth";
import {
  getDocument,
  queryDocuments,
  updateDocument,
  setDocument,
  serverTimestamp,
} from "../../lib/firestore";
import type {
  OffboardFlow,
  FlowTask,
  FlowStatus,
  TaskStatus,
} from "../../types/offboarding.types";
import type { AlumniProfile } from "../../types/alumni.types";
import type { KnowledgeThread } from "../../types/knowledgeThreads.types";
import type { KnowledgeItem } from "../../types/knowledge.types";
import { AskExpertModal } from "../../components/alumni/AskExpertModal";
import { ThreadCard } from "../../components/alumni/ThreadCard";

function toDate(ts: Timestamp | null | undefined): Date | null {
  if (!ts) return null;
  if (typeof (ts as Timestamp).toDate === "function") return (ts as Timestamp).toDate();
  return null;
}

function toAbsoluteUrl(url: string): string {
  if (!url) return "#";
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

function statusBadge(status: FlowStatus) {
  const map: Record<
    FlowStatus,
    { label: string; variant: "teal" | "mist" | "ember" | "amber" }
  > = {
    pending_approval: { label: "Pending Approval", variant: "amber" },
    rejected: { label: "Rejected", variant: "ember" },
    not_started: { label: "Not Started", variant: "mist" },
    in_progress: { label: "In Progress", variant: "teal" },
    completed: { label: "Completed", variant: "teal" },
    cancelled: { label: "Cancelled", variant: "ember" },
  };
  const s = map[status];
  return <Badge variant={s.variant}>{s.label}</Badge>;
}

function taskStatusBadge(status: TaskStatus) {
  const map: Record<TaskStatus, { label: string; variant: "teal" | "mist" | "ember" }> = {
    pending: { label: "Pending", variant: "mist" },
    in_progress: { label: "In Progress", variant: "teal" },
    completed: { label: "Completed", variant: "teal" },
    overdue: { label: "Overdue", variant: "ember" },
    skipped: { label: "Skipped", variant: "mist" },
  };
  const s = map[status];
  return <Badge variant={s.variant}>{s.label}</Badge>;
}

function scoreColor(value: number): "ember" | "amber" | "teal" {
  if (value <= 40) return "ember";
  if (value <= 79) return "amber";
  return "teal";
}

const ROLE_SECTIONS: { role: string; label: string }[] = [
  { role: "hr_admin", label: "HR Tasks" },
  { role: "it_admin", label: "IT Tasks" },
  { role: "manager", label: "Manager Tasks" },
  { role: "employee", label: "Employee Tasks" },
];

const SCORE_CARDS: {
  key: keyof OffboardFlow["completionScores"];
  label: string;
  icon: React.ReactNode;
}[] = [
  { key: "tasks", label: "Tasks", icon: <ClipboardList size={16} /> },
  { key: "knowledge", label: "Knowledge", icon: <BookOpen size={16} /> },
  { key: "accessRevocation", label: "Access Revocation", icon: <Shield size={16} /> },
  { key: "exitInterview", label: "Exit Interview", icon: <MessageSquare size={16} /> },
  { key: "assets", label: "Assets", icon: <Package size={16} /> },
];

export default function OffboardingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { appUser, companyId } = useAuth();

  const [flow, setFlow] = useState<OffboardFlow | null>(null);
  const [tasks, setTasks] = useState<FlowTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "tasks" | "access" | "knowledge" | "assets" | "graph" | "activity"
  >("tasks");
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [linkedAlumni, setLinkedAlumni] = useState<AlumniProfile | null>(null);
  const [threads, setThreads] = useState<KnowledgeThread[]>([]);
  const [showAskModal, setShowAskModal] = useState(false);
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null);
  const [knowledgeItems, setKnowledgeItems] = useState<Array<{ id: string; title: string; type: string }>>([]);
  const [editingUrlTaskId, setEditingUrlTaskId] = useState<string | null>(null);
  const [editingUrlValue, setEditingUrlValue] = useState("");

  const loadData = useCallback(async () => {
    if (!id || !companyId) return;
    try {
      const [flowData, taskData] = await Promise.all([
        getDocument<OffboardFlow>("offboardFlows", id),
        queryDocuments<FlowTask>("flowTasks", [
          // Tenant-scoped: firestore.rules rejects a flowTasks list whose
          // filters do not prove the caller's companyId.
          where("companyId", "==", companyId),
          where("flowId", "==", id),
          orderBy("dueDate", "asc"),
        ]),
      ]);
      if (!flowData) {
        setError("Offboarding not found");
        return;
      }
      setFlow(flowData);
      setTasks(taskData);
    } catch {
      setError("Failed to load offboarding details");
    } finally {
      setLoading(false);
    }
  }, [id, companyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!id || !companyId) return;
    queryDocuments<AlumniProfile>("alumniProfiles", [
      where("flowId", "==", id),
      where("companyId", "==", companyId),
    ]).then((results) => {
      if (results.length > 0 && results[0].optedIn) setLinkedAlumni(results[0]);
    }).catch(() => {});
  }, [id, companyId]);

  useEffect(() => {
    if (!id || !companyId) return;
    queryDocuments<KnowledgeThread>("knowledgeThreads", [
      where("flowId", "==", id),
      where("companyId", "==", companyId),
      orderBy("lastMessageAt", "desc"),
    ]).then(setThreads).catch(() => {});
  }, [id, companyId]);

  useEffect(() => {
    if (!id || !companyId) return;
    queryDocuments<KnowledgeItem>("knowledgeItems", [
      where("flowId", "==", id),
      where("companyId", "==", companyId),
    ]).then((items) => {
      setKnowledgeItems(items.map((i) => ({ id: i.id, title: i.title, type: i.type })));
    }).catch(() => {});
  }, [id, companyId]);

  async function handleCopyPortalLink() {
    if (!flow) return;
    const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    const link = `${baseUrl}/portal/${flow.portalToken}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
    }
  }

  async function handleConfirmComplete(addToAlumni: boolean) {
    if (!flow || !id || !companyId) return;
    setCompleting(true);
    try {
      // 1. Mark flow complete (lastUpdatedBy lets the audit trigger
      // identify the actor).
      await updateDocument("offboardFlows", id, {
        status: "completed",
        completedAt: serverTimestamp(),
        progressPercent: 100,
        "completionScores.tasks": 100,
        lastUpdatedBy: appUser?.id ?? null,
      });

      // 2. The active-offboarding counter is decremented by the
      // onFlowStatusChangedUpdateUsage trigger; clients cannot write
      // usageCount, and a read-modify-write here would race anyway.

      // 3. If alumni opt-in, create alumni record
      if (addToAlumni) {
        const alumniId = crypto.randomUUID();
        await setDocument("alumniProfiles", alumniId, {
          id: alumniId,
          companyId: flow.companyId,
          flowId: flow.id,
          name: flow.employeeName,
          // Lower-cased to match the Firebase Auth address the alumni portal
          // resolves the profile by.
          email: (flow.employeeEmail || "").trim().toLowerCase(),
          role: flow.employeeRole,
          department: flow.employeeDepartment,
          exitDate: flow.lastWorkingDay,
          exitType: "voluntary",
          linkedIn: "",
          currentCompany: "",
          currentRole: "",
          status: "active",
          rehirePriority: "none",
          notes: "",
          tags: [],
          optedIn: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      setShowCompleteModal(false);
      navigate("/offboardings");
    } catch (err) {
      console.error(err);
    } finally {
      setCompleting(false);
    }
  }

  async function handleCancel() {
    if (!flow || !id) return;
    const confirmed = window.confirm(
      `Are you sure you want to cancel ${flow.employeeName}'s offboarding? This action cannot be undone.`
    );
    if (!confirmed) return;
    setCancelling(true);
    try {
      await updateDocument("offboardFlows", id, {
        status: "cancelled",
        lastUpdatedBy: appUser?.id ?? null,
      });
      setFlow({ ...flow, status: "cancelled" });
    } catch {
      // Error
    } finally {
      setCancelling(false);
    }
  }

  async function handleToggleTask(task: FlowTask) {
    if (!flow || !id || !appUser) return;
    const newStatus: TaskStatus =
      task.status === "completed" ? "pending" : "completed";
    const isCompleting = newStatus === "completed";

    // Optimistic update
    const updatedTasks = tasks.map((t) =>
      t.id === task.id
        ? {
            ...t,
            status: newStatus,
            completedAt: isCompleting ? Timestamp.now() : null,
            completedBy: isCompleting ? appUser.id : "",
          }
        : t
    );
    setTasks(updatedTasks);

    // Calculate new progress
    const completedCount = updatedTasks.filter(
      (t) => t.status === "completed"
    ).length;
    const totalCount = updatedTasks.length;
    const progressPercent =
      totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    // Update flow locally
    let newFlowStatus = flow.status;
    if (flow.status === "not_started" && completedCount > 0) {
      newFlowStatus = "in_progress";
    }

    setFlow({
      ...flow,
      progressPercent,
      status: newFlowStatus,
      completionScores: { ...flow.completionScores, tasks: progressPercent },
    });

    try {
      // Update task
      await updateDocument("flowTasks", task.id, isCompleting
        ? { status: "completed", completedAt: serverTimestamp(), completedBy: appUser.id }
        : { status: "pending", completedAt: null, completedBy: "" }
      );

      // Update flow
      if (newFlowStatus !== flow.status) {
        await updateDocument("offboardFlows", id, {
          progressPercent,
          "completionScores.tasks": progressPercent,
          status: newFlowStatus,
        });
      } else {
        await updateDocument("offboardFlows", id, {
          progressPercent,
          "completionScores.tasks": progressPercent,
        });
      }
    } catch {
      // Revert on error
      loadData();
    }
  }

  async function handleSaveTaskUrl(task: FlowTask) {
    const url = editingUrlValue.trim();
    setEditingUrlTaskId(null);
    setEditingUrlValue("");
    if (!url) return;
    setTasks((prev) => prev.map((t) => t.id === task.id ? { ...t, linkUrl: url } : t));
    try {
      await updateDocument("flowTasks", task.id, { linkUrl: url });
    } catch {
      loadData();
    }
  }

  // TODO: Add onSnapshot listener for real-time score updates
  const handleKnowledgeScoreUpdate = (newScore: number) => {
    setFlow((prev) =>
      prev
        ? {
            ...prev,
            completionScores: {
              ...prev.completionScores,
              knowledge: newScore,
            },
          }
        : prev
    );
  };

  const handleRevocationScoreUpdate = (newScore: number) => {
    setFlow((prev) =>
      prev
        ? {
            ...prev,
            completionScores: {
              ...prev.completionScores,
              accessRevocation: newScore,
            },
          }
        : prev
    );
  };

  const handleAssetScoreUpdate = (newScore: number) => {
    setFlow((prev) =>
      prev
        ? {
            ...prev,
            completionScores: {
              ...prev.completionScores,
              assets: newScore,
            },
          }
        : prev
    );
  };

  if (loading) {
    return (
      <div className="py-24 flex justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Defense-in-depth: rely on Firestore rules but also gate the UI so a
  // client-side bypass doesn't render sensitive employee data.
  if (flow && (!companyId || flow.companyId !== companyId)) {
    return (
      <div className="space-y-4">
        <Link
          to="/offboardings"
          className="inline-flex items-center gap-1 text-sm text-mist hover:text-navy transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Offboardings
        </Link>
        <Card>
          <div className="py-12 text-center">
            <p className="text-sm text-ember font-medium">
              You don't have access to this offboarding.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !flow) {
    return (
      <div className="space-y-4">
        <Link
          to="/offboardings"
          className="inline-flex items-center gap-1 text-sm text-mist hover:text-navy transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Offboardings
        </Link>
        <Card>
          <div className="py-12 text-center">
            <p className="text-sm text-ember font-medium">
              {error || "Offboarding not found"}
            </p>
            <Link to="/offboardings">
              <Button variant="outline" className="mt-4">
                Go Back
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  const lwdDate = toDate(flow.lastWorkingDay);
  const daysLeft = lwdDate ? differenceInDays(lwdDate, new Date()) : null;
  const isOverdue = lwdDate ? isPast(lwdDate) : false;
  const canAct =
    !!appUser &&
    flow !== null &&
    flow.status !== "completed" &&
    flow.status !== "cancelled" &&
    flow.status !== "pending_approval" &&
    flow.status !== "rejected";

  // Map app roles to the task assigneeRole values they can complete
  const ROLE_TASK_PERMISSION: Record<string, string | null> = {
    super_admin: null, // null means can complete any task
    hr_admin: "hr_admin",
    it_admin: "it_admin",
    manager: "manager",
  };
  const userTaskRole = appUser ? ROLE_TASK_PERMISSION[appUser.role] : undefined;
  function canToggleTask(task: FlowTask): boolean {
    if (!canAct) return false;
    if (userTaskRole === null) return true; // super_admin
    return task.assigneeRole === userTaskRole;
  }

  // Group tasks by role
  const tasksByRole = ROLE_SECTIONS.map((section) => ({
    ...section,
    tasks: tasks.filter((t) => t.assigneeRole === section.role),
  })).filter((section) => section.tasks.length > 0);

  return (
    <div className="space-y-6">
      {/* Section 1: Header */}
      <div className="space-y-4">
        <Link
          to="/offboardings"
          className="inline-flex items-center gap-1 text-sm text-mist hover:text-navy transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Offboardings
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-full bg-teal/10 flex items-center justify-center text-teal font-display text-xl flex-shrink-0">
              {flow.employeeName.charAt(0)}
            </div>
            <div>
              <h1 className="text-2xl font-display text-navy">
                {flow.employeeName}
              </h1>
              <p className="text-sm text-mist mt-0.5">
                {flow.employeeRole} · {flow.employeeDepartment}
              </p>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {statusBadge(flow.status)}
                <div className="flex items-center gap-1.5 text-xs text-mist">
                  <Calendar size={12} />
                  {lwdDate ? format(lwdDate, "MMM d, yyyy") : "—"}
                </div>
                {daysLeft !== null && (
                  <span
                    className={clsx(
                      "text-xs font-medium",
                      isOverdue ? "text-ember" : daysLeft <= 7 ? "text-amber-600" : "text-teal"
                    )}
                  >
                    {isOverdue
                      ? `${Math.abs(daysLeft)} days ago`
                      : `${daysLeft} days remaining`}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleCopyPortalLink}>
              <LinkIcon size={14} className="mr-1.5" />
              {copied ? "Copied!" : "Copy Portal Link"}
            </Button>
            {canAct && (
              <>
                <Button
                  size="sm"
                  onClick={() => setShowCompleteModal(true)}
                  loading={completing}
                >
                  <CheckCircle size={14} className="mr-1.5" />
                  Mark Complete
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancel}
                  loading={cancelling}
                  className="text-ember hover:text-ember hover:bg-ember/5"
                >
                  <XCircle size={14} className="mr-1.5" />
                  Cancel
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Approval chain — shown only when approvals were configured. */}
      <ApprovalPanel
        flow={flow}
        currentUserId={appUser?.id}
        onUpdated={(patch) => setFlow((prev) => (prev ? { ...prev, ...patch } : prev))}
      />

      {/* Section 2: Completion Scores */}
      <div className="flex gap-4 overflow-x-auto pb-1">
        {SCORE_CARDS.map((card) => {
          const value = flow.completionScores[card.key];
          const color = scoreColor(value);
          const colorMap = {
            ember: "text-ember",
            amber: "text-amber-600",
            teal: "text-teal",
          };
          const progressColor = color === "amber" ? "amber" as const : color;

          return (
            <Card key={card.key} className="min-w-[140px] flex-1">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-mist">
                  {card.icon}
                  <span className="text-xs font-medium">{card.label}</span>
                </div>
                <p className={clsx("text-2xl font-semibold", colorMap[color])}>
                  {value}
                  <span className="text-sm font-normal">%</span>
                  {card.key === "knowledge" && flow.knowledgeGapAnalysis?.analyzedAt && (
                    <span className="text-[10px] text-teal ml-1">AI</span>
                  )}
                </p>
                <Progress value={value} size="sm" color={progressColor} />
              </div>
            </Card>
          );
        })}
      </div>

      {/* Section 3: Tabs */}
      <div className="flex gap-1 bg-navy/5 rounded-md p-1 w-fit">
        <button
          onClick={() => setActiveTab("tasks")}
          className={clsx(
            "px-4 py-2 text-sm font-medium rounded-md transition-colors",
            activeTab === "tasks"
              ? "bg-white text-navy shadow-sm"
              : "text-mist hover:text-navy"
          )}
        >
          Tasks
        </button>
        <button
          onClick={() => setActiveTab("access")}
          className={clsx(
            "px-4 py-2 text-sm font-medium rounded-md transition-colors",
            activeTab === "access"
              ? "bg-white text-navy shadow-sm"
              : "text-mist hover:text-navy"
          )}
        >
          Access Revocation
        </button>
        <button
          onClick={() => setActiveTab("knowledge")}
          className={clsx(
            "px-4 py-2 text-sm font-medium rounded-md transition-colors",
            activeTab === "knowledge"
              ? "bg-white text-navy shadow-sm"
              : "text-mist hover:text-navy"
          )}
        >
          Knowledge
        </button>
        <button
          onClick={() => setActiveTab("assets")}
          className={clsx(
            "px-4 py-2 text-sm font-medium rounded-md transition-colors",
            activeTab === "assets"
              ? "bg-white text-navy shadow-sm"
              : "text-mist hover:text-navy"
          )}
        >
          Assets
        </button>
        <button
          onClick={() => setActiveTab("graph")}
          className={clsx(
            "px-4 py-2 text-sm font-medium rounded-md transition-colors",
            activeTab === "graph"
              ? "bg-white text-navy shadow-sm"
              : "text-mist hover:text-navy"
          )}
        >
          Dependencies
        </button>
        <button
          onClick={() => setActiveTab("activity")}
          className={clsx(
            "px-4 py-2 text-sm font-medium rounded-md transition-colors",
            activeTab === "activity"
              ? "bg-white text-navy shadow-sm"
              : "text-mist hover:text-navy"
          )}
        >
          Activity
        </button>
      </div>

      {/* Tab content */}
      {activeTab === "tasks" && (
        <div className="space-y-6">
          <h2 className="text-lg font-display text-navy">Tasks</h2>

          {tasksByRole.length === 0 ? (
            <Card>
              <p className="text-sm text-mist text-center py-8">
                No tasks found for this offboarding.
              </p>
            </Card>
          ) : (
            tasksByRole.map((section) => {
              const completedInSection = section.tasks.filter(
                (t) => t.status === "completed"
              ).length;

              return (
                <Card key={section.role} padding="none">
                  {/* Section header */}
                  <div className="flex items-center justify-between px-6 py-3 border-b border-navy/5 bg-navy/[0.02]">
                    <h3 className="text-sm font-semibold text-navy">
                      {section.label}{" "}
                      <span className="font-normal text-mist">
                        · {completedInSection}/{section.tasks.length} completed
                      </span>
                    </h3>
                  </div>

                  {/* Task rows */}
                  <div className="divide-y divide-navy/5">
                    {section.tasks.map((task) => {
                      const taskDueDate = toDate(task.dueDate);
                      const taskOverdue =
                        taskDueDate &&
                        isPast(taskDueDate) &&
                        task.status !== "completed" &&
                        task.status !== "skipped";

                      // Tasks that require employee action — checkbox-only toggle is not meaningful
                      const isEmployeeAction =
                        task.type === "signature" ||
                        task.type === "upload";
                      // Admin can manually mark checkbox for form/link/checkbox types
                      const isCheckboxToggleable =
                        !isEmployeeAction && canToggleTask(task);

                      return (
                        <div
                          key={task.id}
                          className="px-6 py-3 space-y-2"
                        >
                          <div className="flex items-start gap-3">
                            {/* Checkbox — disabled for signature/upload (employee must act) */}
                            <button
                              onClick={() => isCheckboxToggleable && handleToggleTask(task)}
                              disabled={!isCheckboxToggleable}
                              title={
                                isEmployeeAction
                                  ? `This task requires the employee to ${task.type === "signature" ? "provide a signature" : "upload a file"} in their portal`
                                  : !canToggleTask(task) && canAct
                                  ? "You can only complete tasks assigned to your role"
                                  : undefined
                              }
                              className={clsx(
                                "mt-0.5 h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                                task.status === "completed"
                                  ? "bg-teal border-teal text-white"
                                  : "border-navy/20 hover:border-teal",
                                (!isCheckboxToggleable) && "opacity-50 cursor-not-allowed"
                              )}
                            >
                              {task.status === "completed" && (
                                <CheckCircle size={12} />
                              )}
                            </button>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <p
                                className={clsx(
                                  "text-sm font-medium",
                                  task.status === "completed"
                                    ? "text-mist line-through"
                                    : "text-navy"
                                )}
                              >
                                {task.title}
                              </p>
                              {task.description && (
                                <p className="text-xs text-mist mt-0.5 line-clamp-2">
                                  {task.description}
                                </p>
                              )}
                            </div>

                            {/* Meta */}
                            <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                              {task.isRequired && (
                                <Badge variant="amber">Required</Badge>
                              )}
                              {taskDueDate && (
                                <span
                                  className={clsx(
                                    "text-xs",
                                    taskOverdue
                                      ? "text-ember font-medium"
                                      : "text-mist"
                                  )}
                                >
                                  {format(taskDueDate, "MMM d")}
                                </span>
                              )}
                              {taskStatusBadge(task.status)}
                            </div>
                          </div>

                          {/* Type-specific indicators */}
                          {task.type === "signature" && (
                            <div className={clsx(
                              "ml-8 flex items-center gap-2 rounded-md px-3 py-2 text-xs",
                              task.status === "completed"
                                ? "bg-teal/10 text-teal"
                                : "bg-amber-50 text-amber-700 border border-amber-200"
                            )}>
                              {task.status === "completed" ? (
                                <>
                                  <CheckCircle size={13} className="flex-shrink-0" />
                                  <span>Signature captured by employee</span>
                                  {task.uploadedFileUrl && (
                                    <a
                                      href={task.uploadedFileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="ml-auto underline font-medium"
                                    >
                                      View
                                    </a>
                                  )}
                                </>
                              ) : (
                                <>
                                  <PenLine size={13} className="flex-shrink-0" />
                                  <span>Awaiting employee signature via their portal</span>
                                </>
                              )}
                            </div>
                          )}

                          {task.type === "upload" && (
                            <div className={clsx(
                              "ml-8 flex items-center gap-2 rounded-md px-3 py-2 text-xs",
                              task.status === "completed"
                                ? "bg-teal/10 text-teal"
                                : "bg-amber-50 text-amber-700 border border-amber-200"
                            )}>
                              {task.status === "completed" ? (
                                <>
                                  <FileText size={13} className="flex-shrink-0" />
                                  <span>File uploaded by employee</span>
                                  {task.uploadedFileUrl && (
                                    <a
                                      href={task.uploadedFileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="ml-auto underline font-medium"
                                    >
                                      Download
                                    </a>
                                  )}
                                </>
                              ) : (
                                <>
                                  <Upload size={13} className="flex-shrink-0" />
                                  <span>Awaiting file upload from employee portal</span>
                                </>
                              )}
                            </div>
                          )}

                          {(task.type === "form" || task.type === "link") && (
                            <div className="ml-8">
                              {task.linkUrl ? (
                                <div className="flex items-center gap-2">
                                  <a
                                    href={toAbsoluteUrl(task.linkUrl)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 text-xs text-teal hover:underline font-medium"
                                  >
                                    <ExternalLink size={12} />
                                    {task.type === "form" ? "Open Form" : "Open Link"}
                                  </a>
                                  <button
                                    onClick={() => { setEditingUrlTaskId(task.id); setEditingUrlValue(task.linkUrl ?? ""); }}
                                    className="text-xs text-mist hover:text-navy underline"
                                  >
                                    Edit URL
                                  </button>
                                  {task.status !== "completed" && canToggleTask(task) && (
                                    <span className="text-xs text-mist">— open then mark complete above</span>
                                  )}
                                </div>
                              ) : editingUrlTaskId === task.id ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="url"
                                    value={editingUrlValue}
                                    onChange={(e) => setEditingUrlValue(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") handleSaveTaskUrl(task); if (e.key === "Escape") { setEditingUrlTaskId(null); setEditingUrlValue(""); } }}
                                    placeholder="https://..."
                                    autoFocus
                                    className="text-xs border border-navy/20 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-teal/50 w-64"
                                  />
                                  <button onClick={() => handleSaveTaskUrl(task)} className="text-xs text-teal font-medium hover:underline">Save</button>
                                  <button onClick={() => { setEditingUrlTaskId(null); setEditingUrlValue(""); }} className="text-xs text-mist hover:underline">Cancel</button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => { setEditingUrlTaskId(task.id); setEditingUrlValue(""); }}
                                  className="text-xs text-ember hover:underline"
                                >
                                  + Set URL (missing)
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })
          )}
        </div>
      )}

      {activeTab === "access" && (
        <AccessRevocationTracker
          flow={flow}
          onScoreUpdate={handleRevocationScoreUpdate}
        />
      )}

      {activeTab === "knowledge" && (
        <div className="space-y-6">
          <KnowledgeTracker
            flow={flow}
            onScoreUpdate={handleKnowledgeScoreUpdate}
          />

          {/* Expert Threads */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-navy">Expert Threads</h3>
                <p className="text-xs text-mist mt-0.5">
                  Ask {flow.employeeName} follow-up questions about their work
                </p>
              </div>
              {linkedAlumni ? (
                <Button size="sm" onClick={() => setShowAskModal(true)}>
                  <MessageCircle size={14} className="mr-1.5" />
                  Ask {flow.employeeName}
                </Button>
              ) : (
                <span className="text-xs text-mist">
                  {flow.status === "completed"
                    ? "Alumni not in network yet"
                    : "Available after offboarding completes"}
                </span>
              )}
            </div>

            {threads.length > 0 ? (
              <div className="space-y-3">
                {threads.map((t) => (
                  <ThreadCard
                    key={t.id}
                    thread={t}
                    isExpanded={expandedThreadId === t.id}
                    onToggle={() =>
                      setExpandedThreadId((prev) => (prev === t.id ? null : t.id))
                    }
                    onClose={(threadId) => {
                      setThreads((prev) =>
                        prev.map((th) =>
                          th.id === threadId ? { ...th, status: "closed" as const } : th
                        )
                      );
                    }}
                    hrUserId={appUser?.id ?? ""}
                    hrUserName={appUser?.displayName ?? "HR Team"}
                  />
                ))}
              </div>
            ) : linkedAlumni ? (
              <div className="border-2 border-dashed border-navy/10 rounded-xl p-6 text-center">
                <p className="text-sm text-mist">No questions asked yet.</p>
                <p className="text-xs text-mist mt-1">
                  Ask {linkedAlumni.name} about their work processes, clients, or tools.
                </p>
              </div>
            ) : null}
          </div>

          {showAskModal && linkedAlumni && (
            <AskExpertModal
              isOpen={showAskModal}
              onClose={() => setShowAskModal(false)}
              companyId={companyId ?? ""}
              flowId={flow.id}
              alumniId={linkedAlumni.id}
              alumniName={linkedAlumni.name}
              alumniEmail={linkedAlumni.email}
              hrUserId={appUser?.id ?? ""}
              hrUserName={appUser?.displayName ?? "HR Team"}
              knowledgeItems={knowledgeItems}
              onCreated={(thread) => {
                setThreads((prev) => [thread, ...prev]);
                setShowAskModal(false);
              }}
            />
          )}
        </div>
      )}

      {activeTab === "assets" && (
        <AssetReturnTracker
          flowId={flow.id}
          companyId={flow.companyId}
          portalToken={flow.portalToken}
          onScoreUpdate={handleAssetScoreUpdate}
        />
      )}

      {activeTab === "graph" && <DependencyGraph tasks={tasks} />}

      {activeTab === "activity" && <AuditLogTab flowId={flow.id} />}

      <CompleteOffboardingModal
        isOpen={showCompleteModal}
        flow={flow}
        onConfirm={handleConfirmComplete}
        onClose={() => setShowCompleteModal(false)}
        loading={completing}
      />
    </div>
  );
}
